/**
 * Undo what an agent filed, as the user, through the existing endpoints:
 * created frames and tasks are deleted, a new project is archived, moved
 * frames go back where they came from, an archive is restored, a restore is
 * archived and a rename takes the old name back. Something already gone
 * counts as undone.
 */
import { isNotFoundError } from "@/api/client";
import { archiveProject, restoreProject, updateProject } from "@/api/spaces";
import { deleteProjectTask } from "@/components/panels/task-actions";
import { translate } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import type { FilingItem, FilingReceipt } from "@/lib/filing-receipt";
import { deleteFrames, moveFramesToProject } from "@/lib/frame-menu-actions";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";

function idsOf(receipt: FilingReceipt, type: FilingItem["type"]): string[] {
  return receipt.items
    .filter((item) => item.type === type)
    .map((item) => item.id);
}

/** One project write; the project list refreshes after it either way. */
async function writeProject(
  projectId: string,
  write: () => Promise<unknown>,
): Promise<boolean> {
  let written = true;
  try {
    await write();
  } catch (error) {
    if (!isNotFoundError(error)) {
      useNotificationStore.getState().addSystemToast({
        id: `filing-undo-failed-${projectId}`,
        title: translate(framesConversationMessages, "filingUndoFailed"),
        level: "error",
      });
      written = false;
    }
  }
  await useProjectStore.getState().fetchAllProjects(true);
  return written;
}

async function undoCreated(receipt: FilingReceipt): Promise<boolean> {
  const frameIds = idsOf(receipt, "frame");
  const results = await Promise.all([
    frameIds.length > 0
      ? deleteFrames(frameIds, receipt.project_id)
      : Promise.resolve(true),
    ...idsOf(receipt, "task").map((taskId) =>
      deleteProjectTask(receipt.project_id, taskId),
    ),
  ]);
  if (receipt.new_project)
    results.push(
      await writeProject(receipt.project_id, () =>
        archiveProject(receipt.project_id),
      ),
    );
  return results.every(Boolean);
}

async function undoMoved(receipt: FilingReceipt): Promise<boolean> {
  const bySource = new Map<string, string[]>();
  for (const item of receipt.items) {
    if (item.type !== "frame" || !item.from_project_id) continue;
    bySource.set(item.from_project_id, [
      ...(bySource.get(item.from_project_id) ?? []),
      item.id,
    ]);
  }
  const results = await Promise.all(
    [...bySource].map(([fromProjectId, frameIds]) =>
      moveFramesToProject(frameIds, fromProjectId, receipt.project_id),
    ),
  );
  return results.every(Boolean);
}

function undoReceipt(receipt: FilingReceipt): Promise<boolean> {
  const projectId = receipt.project_id;
  switch (receipt.op) {
    case "created":
      return undoCreated(receipt);
    case "moved":
      return undoMoved(receipt);
    case "archived":
      return writeProject(projectId, () => restoreProject(projectId));
    case "restored":
      return writeProject(projectId, () => archiveProject(projectId));
    case "renamed": {
      const name = receipt.previous_name;
      if (!name) return Promise.resolve(false);
      return writeProject(projectId, () => updateProject(projectId, { name }));
    }
  }
}

/** Undo a notice's receipts, newest first. True when all went through. */
export async function undoFiling(
  receipts: readonly FilingReceipt[],
): Promise<boolean> {
  let undone = true;
  for (const receipt of [...receipts].reverse()) {
    undone = (await undoReceipt(receipt)) && undone;
  }
  return undone;
}
